import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';

const Container = styled.div`
  background-color: #f7f7f7;
  min-height: 100vh;
`;

const Header = styled.header`
  background-color: white;
  padding: 20px;
  text-align: center;
  border-bottom: 1px solid #eee;
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 1.5em;
  cursor: pointer;

  &:hover {
    transform: translateY(-50%);
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.5em;
  font-family: ${theme.typography.headingFont || 'Poppins, sans-serif'};
  color: ${theme.colors.deepNavy};
`;

const Content = styled.main`
  padding: 20px;
  max-width: 700px;
  margin: 0 auto;
`;

const UpdatedDate = styled.p`
  font-size: 12px;
  color: #808080;
  text-align: center;
  margin: 0 0 24px;
`;

const Section = styled.section`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h2`
  font-family: ${theme.typography.headingFont || 'Poppins, sans-serif'};
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.deepNavy};
  margin: 0 0 12px;
`;

const SectionText = styled.p`
  font-family: ${theme.typography.bodyFont || 'Inter, sans-serif'};
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  margin: 0 0 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionList = styled.ul`
  font-family: ${theme.typography.bodyFont || 'Inter, sans-serif'};
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  margin: 0 0 12px;
  padding-left: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ListItem = styled.li`
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EmailLink = styled.a`
  color: #007bff;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const TermsOfServiceScreen = () => {
  const navigate = useNavigate();

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate(-1)} aria-label="Go back">
          &larr;
        </BackButton>
        <Title>Terms of Service</Title>
      </Header>
      <Content>
        <UpdatedDate>Last updated: February 2026</UpdatedDate>

        <Section>
          <SectionTitle>1. Acceptance of Terms</SectionTitle>
          <SectionText>
            By accessing or using the Lightway application ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service. The Service is operated by Light & Cover ("we," "us," or "our").
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>2. Description of Service</SectionTitle>
          <SectionText>
            Lightway is a mobile-first advocacy logging application that allows users to track, reflect on, and share self-directed advocacy activities across multiple social justice causes. The Service is provided as a web application accessible through modern web browsers.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>3. User Accounts</SectionTitle>
          <SectionText>
            To use the Service, you must create an account using a valid email address and password. You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. Each individual should maintain only one account. You must notify us immediately of any unauthorized use of your account.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>4. User Content</SectionTitle>
          <SectionText>
            You retain ownership of all content you create, upload, or submit through the Service, including activity logs, reflections, and photos ("User Content"). By submitting User Content, you grant Light & Cover a limited, non-exclusive license to store, display, and process your content solely for the purpose of providing the Service to you.
          </SectionText>
          <SectionText>
            You are solely responsible for the content you submit. You agree not to submit content that is unlawful, defamatory, harassing, or otherwise objectionable.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>5. Acceptable Use</SectionTitle>
          <SectionText>
            When using the Service, you agree not to:
          </SectionText>
          <SectionList>
            <ListItem>Use the Service for any unlawful purpose or in violation of any applicable laws</ListItem>
            <ListItem>Harass, threaten, or intimidate other users</ListItem>
            <ListItem>Impersonate any person or entity</ListItem>
            <ListItem>Submit false, misleading, or fraudulent information</ListItem>
            <ListItem>Attempt to gain unauthorized access to the Service or its related systems</ListItem>
            <ListItem>Interfere with or disrupt the integrity or performance of the Service</ListItem>
            <ListItem>Use automated means to access the Service without our permission</ListItem>
          </SectionList>
        </Section>

        <Section>
          <SectionTitle>6. Privacy</SectionTitle>
          <SectionText>
            Your use of the Service is also governed by our Privacy Policy, which describes how we collect, use, and protect your personal information. By using the Service, you consent to the practices described in the Privacy Policy.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>7. Intellectual Property</SectionTitle>
          <SectionText>
            The Lightway name, logo, visual design, and all associated branding are the property of Light & Cover. You may not use, reproduce, or distribute our trademarks or copyrighted materials without prior written consent. The Service's source code, design elements, and documentation are protected by copyright and other intellectual property laws.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>8. Termination</SectionTitle>
          <SectionText>
            You may delete your account at any time through the Privacy Settings screen. Account deletion includes a 21-day grace period during which you can restore your account by logging back in. After the grace period, your data will be permanently deleted.
          </SectionText>
          <SectionText>
            We reserve the right to suspend or terminate your account if you violate these Terms or engage in conduct that we determine, in our sole discretion, is harmful to other users or to the Service.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>9. Disclaimer of Warranties</SectionTitle>
          <SectionText>
            The Service is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or completely secure.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>10. Limitation of Liability</SectionTitle>
          <SectionText>
            To the maximum extent permitted by applicable law, Light & Cover shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or data, arising out of or in connection with your use of the Service, whether based on warranty, contract, tort, or any other legal theory.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>11. Changes to Terms</SectionTitle>
          <SectionText>
            We may update these Terms from time to time. When we make changes, we will update the "Last updated" date at the top of this page. Your continued use of the Service after any changes constitutes your acceptance of the updated Terms. We encourage you to review these Terms periodically.
          </SectionText>
        </Section>

        <Section>
          <SectionTitle>12. Contact</SectionTitle>
          <SectionText>
            If you have any questions about these Terms, please contact us at{' '}
            <EmailLink href="mailto:dev@lightandcover.org">dev@lightandcover.org</EmailLink>.
          </SectionText>
        </Section>
      </Content>
    </Container>
  );
};
